import { useEffect, useState } from 'react';
import LoginPage from './components/LoginPage';
import Dashboard from './components/Dashboard';
import { api } from './api';

export default function App() {
  const [booting, setBooting] = useState(true);
  const [authState, setAuthState] = useState({ authenticated: false, setup_required: false, user: null });
  const [projects, setProjects] = useState([]);
  const [jobs, setJobs] = useState([]);
  const [config, setConfig] = useState({ openai_api_key_set: false, default_model: 'gpt-5.2-codex', panel_name: 'GEO Panel' });
  const [selectedProjectId, setSelectedProjectId] = useState(null);

  async function refreshProjects() {
    const items = await api.listProjects();
    setProjects(items);
    if (!selectedProjectId && items.length) {
      setSelectedProjectId(items[0].id);
    }
    return items;
  }

  async function refreshConfig() {
    const nextConfig = await api.getConfig();
    setConfig(nextConfig);
    return nextConfig;
  }

  async function refreshJobs() {
    const items = await api.listJobs({ limit: 100 });
    setJobs(items);
    return items;
  }

  async function bootstrap() {
    setBooting(true);
    try {
      const me = await api.me();
      setAuthState(me);
      if (me.authenticated) {
        await Promise.all([refreshProjects(), refreshConfig(), refreshJobs()]);
      }
    } catch (error) {
      const health = await api.health();
      setAuthState({
        authenticated: false,
        setup_required: health.setup_required,
        user: null,
      });
    } finally {
      setBooting(false);
    }
  }

  useEffect(() => {
    bootstrap();
  }, []);

  useEffect(() => {
    if (!authState.authenticated) {
      return undefined;
    }
    const hasActiveJobs = jobs.some((job) => ['queued', 'running'].includes(job.status));
    if (!hasActiveJobs) {
      return undefined;
    }
    const timer = setInterval(() => {
      refreshJobs();
      refreshProjects();
    }, 3000);
    return () => clearInterval(timer);
  }, [authState.authenticated, jobs]);

  async function handleLogin(payload) {
    if (authState.setup_required) {
      await api.setup(payload);
    } else {
      await api.login(payload);
    }
    await bootstrap();
  }

  async function handleLogout() {
    await api.logout();
    setProjects([]);
    setJobs([]);
    setSelectedProjectId(null);
    await bootstrap();
  }

  async function handleSaveConfig(payload) {
    await api.saveConfig(payload);
    await refreshConfig();
  }

  async function handleProjectSaved(payload) {
    const project = await api.saveProject(payload);
    const items = await refreshProjects();
    const match = items.find((item) => item.id === project.id);
    if (match) {
      setSelectedProjectId(match.id);
    }
    return project;
  }

  async function handleRunLogged(payload) {
    const run = await api.upsertRun(payload);
    await refreshProjects();
    return run;
  }

  async function handleExecuteRun(payload) {
    const result = await api.executeRun(payload);
    await refreshJobs();
    return result.job;
  }

  async function handleExecuteBatch(payload) {
    const result = await api.createBatchJobs(payload);
    await refreshJobs();
    return result;
  }

  if (booting) {
    return (
      <div style={{ minHeight: '100vh', background: 'var(--bg-deep)', display: 'grid', placeItems: 'center', color: 'var(--text-2)', fontFamily: 'var(--font-mono)' }}>
        booting panel...
      </div>
    );
  }

  if (!authState.authenticated) {
    return <LoginPage setupRequired={authState.setup_required} onSubmit={handleLogin} />;
  }

  return (
    <Dashboard
      authState={authState}
      config={config}
      jobs={jobs}
      projects={projects}
      selectedProjectId={selectedProjectId}
      onSelectProject={setSelectedProjectId}
      onLogout={handleLogout}
      onSaveConfig={handleSaveConfig}
      onSaveProject={handleProjectSaved}
      onLogRun={handleRunLogged}
      onExecuteRun={handleExecuteRun}
      onExecuteBatch={handleExecuteBatch}
      onRefreshProjects={refreshProjects}
      onRefreshJobs={refreshJobs}
    />
  );
}
